import React, { useState } from 'react';

import { Typography } from '@mui/material';
import NextImage from 'next/image';
import { Image } from '@/components/shared';
import { useTranslations } from 'next-intl';

type Props = {
    review: {
        author_name: string,
        author_url: string,
        profile_photo_url: string,
        rating: number,
        relative_time_description: string,
        text: string,
        time: number,
        pretty_time: string,
    }
    theme?: 'light' | 'dark'
    disableReadMore?: boolean
    className?: string
}

const CustomGoogleReview = ({ review, theme = 'light', disableReadMore = false, className = '' }: Props) => {
    const t = useTranslations();

    const [expanded, setExpanded] = useState(false);

    const toggleExpand = () => {
        setExpanded(!expanded);
    };

    return (
        <div className={`h-fit p-6 rounded-3xl border border-gray-t-10 ${theme === 'light' ? 'bg-white' : 'bg-primary-gray-950'} ${className}`}>
            <div className="flex items-center gap-x-3">
                <NextImage
                    referrerPolicy='no-referrer'
                    src={review.profile_photo_url}
                    alt="Reviewer Image"
                    width={40}
                    height={40}
                    className="w-10 h-10 rounded-full object-contain"
                />

                <div className="flex flex-col">
                    <Typography variant='h6' component='h2' className="line-clamp-1 leading-normal text-base">
                        <a href={review.author_url} target="_blank" rel='noopener noreferrer' className="text-black hover:underline font-roboto capitalize">
                            {review.author_name}
                        </a>
                    </Typography>

                    <p title={review.pretty_time} className="text-sm cursor-default text-gray-t-90 font-roboto">{review.relative_time_description}</p>
                </div>
            </div>

            <div className="flex items-center gap-x-0.5 mt-4">
                {Array.from({ length: review.rating }).map((_, i) => (
                    <Image
                        key={i}
                        src="star_lc1nez"
                        alt="Star"
                        width={20}
                        height={20}
                        className='w-5 h-5'
                    />
                ))}
            </div>

            <p className={`mt-2 ${expanded ? 'line-clamp-none' : 'line-clamp-3'} font-roboto text-black`}>
                {review.text}
            </p>

            {!disableReadMore && (
                <span onClick={toggleExpand} className="text-blue-600 underline cursor-pointer font-roboto">
                    {expanded ? t('shared.read_less') : t('shared.read_more')}
                </span>
            )}
        </div>
    );
}

export default CustomGoogleReview;